import '@fontsource-variable/nunito';
import '@fontsource-variable/jetbrains-mono';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import './theme/tokens.css';
import './theme/readonly.css';

import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { ApiRequestError } from './api/client';
import { ME_QUERY_KEY, reasksSession } from './api/session';
import { App } from './App';
import { ErrorFallback } from './components/ErrorFallback';
import { initErrorTracking, isTransientNetworkError } from './lib/errorTracking';
import { theme } from './theme';

const reportCacheError = (error: unknown, failedKey?: readonly unknown[]) => {
  // The session ended — revoked on Uporabniki, or expired — or a route refused
  // the role (SPEC §1.1L): a bug in the gating, or a role changed while the
  // stream was down and the `access` event never arrived. Asking who we are
  // again sends the layout to /login, or refreshes the capabilities so the
  // layout's role effect drops every cache and the screens redraw. Not while
  // that question is already on its way: a second ask would only cancel it.
  if (reasksSession(error, failedKey) && queryClient.isFetching({ queryKey: ME_QUERY_KEY }) === 0) {
    void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }
  if (import.meta.env.DEV && error instanceof ApiRequestError && error.code === 'capability') {
    console.warn(`[access] refused: ${error.message}`, error);
  }
  if (error instanceof ApiRequestError && error.status < 500) return;
  if (isTransientNetworkError(error)) return;
  Sentry.captureException(error);
};

const queryCache = new QueryCache({ onError: (error, query) => reportCacheError(error, query.queryKey) });
const mutationCache = new MutationCache({ onError: (error) => reportCacheError(error) });

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

// Rendering waits for error tracking, so a crash in the first render still reaches GlitchTip.
void initErrorTracking().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <MantineProvider theme={theme}>
          <Notifications position="top-right" />
          <Sentry.ErrorBoundary fallback={({ error }) => <ErrorFallback error={error as Error} />}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </Sentry.ErrorBoundary>
        </MantineProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});
